import { Action, CombinedState, combineReducers, configureStore, Reducer, ThunkAction, ThunkDispatch } from '@reduxjs/toolkit';
import { scenePlugins } from '@ff-os/pdp-shared-registry/index';
import { publicAssets } from './apis/publicAssets';
import { reducer as auth } from './slices/authSlice';
import { reducer as compatibility } from './slices/compatibilitySlice';
import { reducer as externalUploaders } from './slices/externalUploadersSlice';
import { reducer as fonts } from './slices/fontsSlice';
import { reducer as global } from './slices/globalSlice';
import { reducer as projectLoading } from './slices/projectLoadingSlice';
import { reducer as resources } from './slices/resourcesSlice';
import { reducer as scene } from './slices/sceneSlice';
import { reducer as snapshot } from './slices/snapshotSlice';
import { reducer as tenant } from './slices/tenantSlice';

export const createReducer = <TProject>(reducers: { project: Reducer<CombinedState<TProject>>; ui: Reducer }) => {
    return {
        [publicAssets.reducerPath]: publicAssets.reducer,
        global,
        scene,
        tenant,
        fonts,
        resources,
        snapshot,
        auth,
        projectLoading,
        compatibility,
        externalUploaders,
        ui: reducers.ui,
        project: reducers.project
    };
};

export const createStore = (state: PdpState | undefined = undefined) => {

    const pluginReducers = state
        ? scenePlugins.require(state?.scene.pluginId).reducers
        : combineReducers({}) as unknown as { project: Reducer; ui: Reducer };

    return configureStore({
        preloadedState: state,
        reducer: createReducer(pluginReducers),
        middleware: (getDefault) => getDefault().concat(publicAssets.middleware)
    });
};

export type PdpStore<TProject = unknown> = ReturnType<StateTypes<TProject>['Store']>;
export type PdpState<TProject = unknown> = ReturnType<PdpStore<TProject>['getState']>;
export type PdpDispatch<TProject = unknown> = ThunkDispatch<PdpState<TProject>, any, Action>;

export type PdpThunk<TProject = unknown, ReturnType = void> = ThunkAction<ReturnType,
    PdpState<TProject>,
    any,
    Action<string>>;

export type AppThunkApi<TProject = unknown> = {
    getState: () => PdpState<TProject>;
    dispatch: PdpDispatch<TProject>;
};

export type AppThunkApiConfig<TProject> = {
    state: PdpState<TProject>;
    dispatch: PdpDispatch<TProject>;
    rejectValue: Reject;
};

export type Reject =
    | { rejectType: 'http'; statusCode: number };

// Typing from: https://stackoverflow.com/questions/52963637/typescript-is-it-possible-to-get-the-return-type-of-a-generic-function
// Can't use `type PdpStore<TProject> = typeof Store<T>` due to missing support in babel-loader and eslint
// This is supported since typescript 4.7.2
class StateTypes<TProject> {
    Store = (project: Reducer<CombinedState<TProject>>, ui: Reducer) => {
        return configureStore({
            reducer: createReducer({ ui, project }),
            middleware: m => m()
        });
    };
}